import moment from 'moment';
import { Pim } from '@mummssoftware/utils';
import { SitePatient } from './types';

/** pimreader rpc date params are MM/DD/YYYY. */
const PIMREADER_DATE_PARAM = 'MM/DD/YYYY';

export const toPimreaderDate = (isoDate: string): string =>
  moment(isoDate, 'YYYY-MM-DD').format(PIMREADER_DATE_PARAM);

/** getsitecareplans row → the scheduler care plan shape the calendar scores. */
export const sitePlanRowToCarePlan = (
  row: mumms.SiteCarePlanRow,
): mumms.patientCarePlanProblemFrequency =>
  (({
    id: row.id,
    patientNumber: row.patientnumber,
    roleGroupId: row.rolegroup_id as number,
    roleId: row.role_id,
    discipline: row.discipline,
    effectiveDate: row.effectivedate,
    endDate: row.enddate,
    frequency: row.frequency,
    period: row.period ?? '',
    frequencyText: row.frequencytext ?? '',
    frequencyFirstPartial: row.frequencyfirstpartial as null,
    daysOfWeek: row.daysofweek,
    daysOfMonth: null,
    prn: row.prn,
    siteId: row.site_id as number,
    tenantId: '',
    userComments: null,
    workerId: [],
    recurringPattern: [],
    recurringSeparationCount: null,
    overwriteFuturePlan: false,
  } as unknown) as mumms.patientCarePlanProblemFrequency);

/** getsitevisits row → the pimreader visit fields the calendar reads. */
export const siteVisitRowToVisit = (
  row: mumms.SiteVisitRow,
): mumms.pimreaderVisit =>
  (({
    id: row.id,
    patient_id: String(row.patient_id),
    patientnumber: row.patientnumber,
    patientlastname: row.patientlastname ?? '',
    patientfirstname: row.patientfirstname ?? '',
    person_id: String(row.person_id),
    role_id: row.role_id as number,
    rolename: row.rolename ?? '',
    rolegroup_id: row.rolegroup_id as number,
    rolegroupname: row.rolegroupname ?? '',
    effectivedate: row.effectivedate,
    effectivedatetime_shifted: row.effectivedatetime_shifted,
    starteventdatetime_shifted: row.starteventdatetime_shifted ?? '',
    status: row.status ?? '',
    visittype: row.visittype ?? '',
  } as unknown) as mumms.pimreaderVisit);

export const patientListingToSitePatients = (
  patients: mumms.patient[] | null | undefined,
): SitePatient[] =>
  (patients || [])
    .filter((p) => !!p?.patientNumber)
    .map((p) => ({
      patientNumber: String(p.patientNumber),
      patientId: p.id != null ? Number(p.id) : null,
      lastName: p.lastName || '',
      firstName: p.firstName || '',
      siteId: p.siteId != null ? Number(p.siteId) : null,
    }));

export type SiteComplianceData = {
  carePlans: mumms.patientCarePlanProblemFrequency[];
  visits: mumms.pimreaderVisit[];
};

/**
 * Two site-wide pimreader calls (OP-33263 getsitecareplans / getsitevisits)
 * instead of two calls per patient. `pim` must have been created with a
 * pgRestUrl; without one the Pim wrappers resolve to null.
 */
export const fetchSiteComplianceData = async (
  pim: Pim,
  siteId: string,
  fromDate: string,
  toDate: string,
): Promise<SiteComplianceData> => {
  const from = toPimreaderDate(fromDate);
  const to = toPimreaderDate(toDate);
  // Sequential on purpose: the per-tenant pgrest pods run with a 100 MiB
  // limit and serialising two site-wide responses at once OOM-kills them on
  // large sites (king: 17k visit rows). One at a time costs about the same
  // wall time, since the pod is CPU-bound, and never two buffers at once.
  const plans = await pim.getSiteCarePlans(siteId, from, to);
  const visits = await pim.getSiteVisits(siteId, from, to);
  if (plans === null || visits === null) {
    throw new Error('pimreader is not configured (no pgRestUrl)');
  }
  return {
    carePlans: (plans || [])
      .filter((row) => row.rolegroup_id != null)
      .map(sitePlanRowToCarePlan),
    visits: (visits || []).map(siteVisitRowToVisit),
  };
};
